
import React from 'react';
import {
    Navigate,
    useLocation,
    useNavigate,
    useOutletContext,
    useParams,
    useRoutes
} from 'react-router-dom';
// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
// pages
import LoginPage from './pages/LoginPage';
import Page404 from './pages/Page404';
import DashboardAppPage from './pages/DashboardAppPage';
// grid pages
import ReportTypesPage from './pages/grids/ReportTypesPage';
import DocsLayout from './layouts/DocsLayout';
import PrivacyPolicy from './pages/docs/PrivacyPolicy';
import TermsOfService from './pages/docs/TermsOfService';
import PageNotAuth from './pages/PageNotAuth';
import PermissionEditorPage from './pages/grids/PermissionEditorPage';
import ReportPreview from './pages/ReportPreview';
import UsersPage from './pages/grids/UsersPage';
import PaymentInstancesPage from './pages/grids/payment/PaymentInstancesPage';
import PayoutInstancesPage from './pages/grids/payout/PayoutInstancesPage';
import CardsWhitelistsPage from './pages/grids/CardsWhitelistsPage';
import PaymentEventsPage from './pages/grids/events/PaymentEventsPage';
import PaymentRefundsPage from './pages/grids/payment/PaymentRefundsPage';
import PayoutEventsPage from './pages/grids/events/PayoutEventsPage';
import FiltersBinsPage from './pages/grids/settings/FiltersBinsPage';
import PaymentOrdersPage from './pages/grids/payment/PaymentOrdersPage';
import PayoutOrdersPage from './pages/grids/payout/PayoutOrdersPage';
import MerchantsPage from './pages/grids/merchant/MerchantsPage';
import MerchantTerminalGatewaysPage from './pages/grids/merchant/MerchantTerminalGatewaysPage';
import MerchantTerminalsPage from './pages/grids/merchant/MerchantTerminalsPage';
import CurrencyConversionRatePage from './pages/grids/CurrencyConversionRatePage';
import GatewaysPage from './pages/grids/gateways/GatewaysPage';
import AccountingExternalTransactionsPage from './pages/grids/accounting/AccountingExternalTransactionsPage';
import AccountingSystemTransactionsPage from './pages/grids/accounting/AccountingSystemTransactionsPage';
import AccountingSystemAccountsPage from './pages/grids/accounting/AccountingSystemAccountsPage';
import CountriesPage from './pages/grids/CountriesPage';
import TerminalShopWindowsPage from './pages/grids/gateways/TerminalShopWindowsPage';
import ChargebacksPage from './pages/grids/payment/ChargebacksPage';
import CommissionsPage from './pages/grids/settings/CommissionsPage';
import CryptoPaymentsPage from './pages/grids/payment/CryptoPaymentsPage';
import CommissionTypesPage from './pages/grids/settings/CommissionTypesPage';
import CryptoCoinsPage from './pages/grids/settings/CryptoCoinsPage';
import FraudsPage from './pages/grids/payment/FraudsPage';
import MerchantTerminalPayoutRequestsPage from './pages/grids/merchant/MerchantTerminalPayoutRequestsPage';
import RolesActionsPermissionsPage from './pages/grids/RolesActionsPermissionsPage';
import P2PEventsPage from './pages/grids/events/P2PEventsPage';
import P2PInstancesPage from './pages/grids/payment/P2PInstancesPage';
import UrlCallbacksPage from './pages/grids/events/UrlCallbacksPage';
import GatewaySettingsPage from './pages/grids/gateways/GatewaySettingsPage';
import GatewayApisPage from './pages/grids/gateways/GatewayApisPage';
import WithdrawalTransactionsPage from './pages/grids/accounting/WithdrawalTransactionsPage';
import AccountingGatewayTransactionsPage from './pages/grids/accounting/AccountingGatewayTransactionsPage';
import RolesPage from './pages/grids/RolesPage';
import AppealsPage from './pages/grids/payment/AppealsPage';
import AgentTransactionsPage from './pages/grids/agents/AgentTransactionsPage';
import AgentsPage from './pages/grids/agents/AgentsPage';
import CascadeRulesPage from './pages/grids/settings/CascadeRulesPage';
import ProvidersPage from './pages/grids/providers/ProvidersPage';
import AccountingGatewayAccountsPage from './pages/grids/accounting/AccountingGatewayAccountsPage';
import GatewayTemplatesPage from './pages/grids/gateways/GatewayTemplatesPage';
import AccountingValidationTasksPage from './pages/grids/accounting/AccountingValidationTasksPage';
import ExportCsvTasksPage from './pages/grids/settings/ExportCsvTasksPage';
import FiltersBanksPage from './pages/grids/settings/FiltersBanksPage';
import FiltersUsersPage from './pages/grids/settings/FiltersUsersPage';
import FiltersIpListPage from './pages/grids/settings/FiltersIpListPage';

// ----------------------------------------------------------------------

/**
 * @param { ReactComponent } Component is component that will be rendered
 * @param { object } optionalProps is temporary solution for passing props to component
 * @returns {function(*): *} Component with route props
 * */
function withRouteProps(Component, optionalProps = {}) {
    function RouteWrapper(props) {
        const outletCtx = useOutletContext() || {};
        const { setHeaderActions, clearHeaderActions } = outletCtx;

        const params = useParams();
        const navigate = useNavigate();
        const location = useLocation();

        return (
            <Component
                {...props}
                {...optionalProps}
                params={params}
                navigate={navigate}
                location={location}
                setHeaderActions={setHeaderActions}
                clearHeaderActions={clearHeaderActions}
            />
        );
    }
    RouteWrapper.displayName = `withRouteProps(${Component.displayName || Component.name || 'Component'})`;
    return RouteWrapper;
}

export default function Router(props) {
    return useRoutes([
        {
            path: '/app/',
            element: <DashboardLayout />,
            children: [
                { element: <Navigate to="/app/dashboard" />, index: true },
                {
                    path: 'dashboard',
                    Component: withRouteProps(DashboardAppPage, props)
                },
                { path: 'users', Component: withRouteProps(UsersPage, props) },
                {
                    path: 'public-merchant-terminal-payout-requests',
                    Component: withRouteProps(
                        MerchantTerminalPayoutRequestsPage,
                        props
                    )
                },
                {
                    path: 'report-types',
                    Component: withRouteProps(ReportTypesPage, props)
                },
                {
                    path: 'role-permissions',
                    Component: withRouteProps(PermissionEditorPage, props)
                },
                {
                    path: 'roles-actions-permissions',
                    Component: withRouteProps(
                        RolesActionsPermissionsPage,
                        props
                    )
                },
                {
                    path: 'filters-bins',
                    Component: withRouteProps(FiltersBinsPage, props)
                },
                {
                    path: 'filters-banks',
                    Component: withRouteProps(FiltersBanksPage, props)
                },
                {
                    path: 'filters-users',
                    Component: withRouteProps(FiltersUsersPage, props)
                },
                {
                    path: 'filters-ip-list',
                    Component: withRouteProps(FiltersIpListPage, props)
                },
                {
                    path: 'cards-whitelist',
                    Component: withRouteProps(CardsWhitelistsPage, props)
                },

                {
                    path: 'payment-instances',
                    Component: withRouteProps(PaymentInstancesPage, props)
                },
                {
                    path: 'payment-events',
                    Component: withRouteProps(PaymentEventsPage, props)
                },
                {
                    path: 'payment-refunds',
                    Component: withRouteProps(PaymentRefundsPage, props)
                },
                {
                    path: 'payment-orders',
                    Component: withRouteProps(PaymentOrdersPage, props)
                },

                {
                    path: 'payout-instances',
                    Component: withRouteProps(PayoutInstancesPage, props)
                },
                {
                    path: 'payout-events',
                    Component: withRouteProps(PayoutEventsPage, props)
                },
                {
                    path: 'payout-orders',
                    Component: withRouteProps(PayoutOrdersPage, props)
                },
                {
                    path: 'merchants',
                    Component: withRouteProps(MerchantsPage, props)
                },
                {
                    path: 'merchant-terminal-gateways',
                    Component: withRouteProps(
                        MerchantTerminalGatewaysPage,
                        props
                    )
                },
                {
                    path: 'merchant-terminals',
                    Component: withRouteProps(MerchantTerminalsPage, props)
                },
                {
                    path: 'currency-conversion-rate',
                    Component: withRouteProps(CurrencyConversionRatePage, props)
                },
                {
                    path: 'gateways',
                    Component: withRouteProps(GatewaysPage, props)
                },
                {
                    path: 'external-transactions',
                    Component: withRouteProps(
                        AccountingExternalTransactionsPage,
                        props
                    )
                },
                {
                    path: 'system-accounts',
                    Component: withRouteProps(AccountingSystemAccountsPage, props)
                },
                {
                    path: 'system-transactions',
                    Component: withRouteProps(
                        AccountingSystemTransactionsPage,
                        props
                    )
                },
                {
                    path: 'countries',
                    Component: withRouteProps(CountriesPage, props)
                },
                {
                    path: 'terminal-shop-windows',
                    Component: withRouteProps(TerminalShopWindowsPage, props)
                },
                {
                    path: 'chargebacks',
                    Component: withRouteProps(ChargebacksPage, props)
                },
                {
                    path: 'commission-types',
                    Component: withRouteProps(CommissionTypesPage, props)
                },
                {
                    path: 'commissions',
                    Component: withRouteProps(CommissionsPage, props)
                },
                {
                    path: 'crypto-coins',
                    Component: withRouteProps(CryptoCoinsPage, props)
                },
                {
                    path: 'crypto-payments',
                    Component: withRouteProps(CryptoPaymentsPage, props)
                },
                {
                    path: 'fraud',
                    Component: withRouteProps(FraudsPage, props)
                },
                {
                    path: 'p2p-events',
                    Component: withRouteProps(P2PEventsPage, props)
                },
                {
                    path: 'p2p-instances',
                    Component: withRouteProps(P2PInstancesPage, props)
                },
                {
                    path: 'url-callbacks',
                    Component: withRouteProps(UrlCallbacksPage, props)
                },
                {
                    path: 'gateway-apis',
                    Component: withRouteProps(GatewayApisPage, props)
                },
                {
                    path: 'gateway-settings',
                    Component: withRouteProps(GatewaySettingsPage, props)
                },
                {
                    path: 'accounting-gateway-transactions',
                    Component: withRouteProps(
                        AccountingGatewayTransactionsPage,
                        props
                    )
                },
                {

                    path: 'accounting-gateway-accounts',
                    Component: withRouteProps(
                        AccountingGatewayAccountsPage,
                        props
                    )
                },
                {
                    path: 'withdrawal-transactions',
                    Component: withRouteProps(
                        WithdrawalTransactionsPage,
                        props
                    )
                },
                {
                    path: 'roles',
                    Component: withRouteProps(RolesPage, props)
                },
                {
                    path: 'appeals',
                    Component: withRouteProps(AppealsPage, props)
                },
                {
                    path: 'agent-transactions',
                    Component: withRouteProps(AgentTransactionsPage, props)
                },
                {
                    path: 'agents',
                    Component: withRouteProps(AgentsPage, props)
                },
                {
                    path: 'cascade-rules',
                    Component: withRouteProps(CascadeRulesPage, props)
                },
                {
                    path: 'providers',
                    Component: withRouteProps(ProvidersPage, props)
                },
                {
                    path: 'gateway-templates',
                    Component: withRouteProps(GatewayTemplatesPage, props)
                },
                {
                    path: 'accounting-validation-tasks',
                    Component: withRouteProps(AccountingValidationTasksPage, props)
                },
                {
                    path: '/app/export-csv-tasks',
                    Component: withRouteProps(ExportCsvTasksPage, props)
                },

            ]
        },
        {
            path: 'login',
            Component: withRouteProps(LoginPage, props)
        },
        {
            path: '/reportTypes/:reportId',
            Component: withRouteProps(ReportPreview, props)
        },
        {
            path: '/doc/api-static.html',
            element: () => {
                window.location.href = '/doc/api-static.html';
                return null;
            }
        },
        {
            element: <SimpleLayout />,
            children: [
                { element: <Navigate to="/app/dashboard" />, index: true },
                {
                    path: '/not-auth',
                    Component: withRouteProps(PageNotAuth, props)
                },
                { path: '404', Component: withRouteProps(Page404, props) },
                { path: '*', element: <Navigate to="/404" /> }
            ]
        },
        {
            path: 'docs',
            element: <DocsLayout />,
            children: [
                { path: '', element: <Navigate to="/docs/privacy" /> },
                {
                    path: 'privacy',
                    Component: withRouteProps(PrivacyPolicy, props)
                },
                {
                    path: 'tos',
                    Component: withRouteProps(TermsOfService, props)
                },
                { path: '*', element: <Navigate to="/docs/privacy" /> }
            ]
        },
        {
            path: '*',
            element: <Navigate to="/404" replace />
        }
    ]);
}

